import React, { useRef, useEffect } from 'react';
import SETTING_KEYS from '../../constants/setting-keys';
import { useImplantContext } from '../../store/implant-context';
import Conditional from './Conditional';
import { AutofilledFieldsType } from '../../utils/pdfExtractor';
import Button, { ButtonType } from '../general-components/Button';

const settings = [
    SETTING_KEYS.waitingPeriod,
    SETTING_KEYS.fieldStrength,
    SETTING_KEYS.mriArc,
    SETTING_KEYS.prep,
    SETTING_KEYS.slewRate,
    SETTING_KEYS.brms,
    SETTING_KEYS.coilType,
    SETTING_KEYS.spatialGradient,
    SETTING_KEYS.sar,
    SETTING_KEYS.artefacts,
    SETTING_KEYS.temp,
];

interface ConditionalsProps {
    fieldStrengths: any[];
    autofilledFields: AutofilledFieldsType;
    showWarningIcon: boolean;
    onSetShowWarningIcon: (value: boolean) => void;
    onValidationError?: (errorString: string) => void;
}

const Conditionals: React.FC<ConditionalsProps> = ({
    fieldStrengths,
    autofilledFields,
    showWarningIcon,
    onSetShowWarningIcon,
    onValidationError,
}) => {
    const {
        updateConditionalField,
        conditionals,
        info,
        addConditional,
        deleteConditional,
        duplicateConditional,
    } = useImplantContext();
    const itemsRef = useRef<Array<HTMLTextAreaElement | null>>([]);

    useEffect(() => {
        itemsRef.current = itemsRef.current.slice(0, conditionals.length);
        for (let i of itemsRef.current) {
            if (i) {
                i.style.height = '' + i.scrollHeight + 'px';
            }
        }
    }, [conditionals.length]);

    const handleFieldStrengthChange = (
        conditionalId: string,
        value: any,
        checked: boolean
    ) => {
        const conditional = conditionals.find(
            (c: any) => c.id === conditionalId || c._id === conditionalId
        );
        if (!conditional) return;

        const fieldStrength = conditional[SETTING_KEYS.fieldStrength];

        let updatedFieldStrength;
        if (checked) {
            updatedFieldStrength = [...fieldStrength, parseFloat(value)];
        } else {
            updatedFieldStrength = fieldStrength.filter(
                (i: any) => i !== parseFloat(value)
            );
        }

        updateConditionalField(
            conditionalId,
            SETTING_KEYS.fieldStrength,
            updatedFieldStrength
        );
    };

    // Validation function for conditionals
    const validateConditionals = (): string => {
        let errorConStr = '';
        let errorFlag = false;

        // Check both new regionalInfos structure and old direct mriStatus for backward compatibility
        const shouldValidate =
            info.regionalInfos?.some(
                (regionalInfo: any) => parseInt(regionalInfo.mriStatus) === 2
            ) || parseInt(info[SETTING_KEYS.mriStatus]) === 2;

        if (shouldValidate) {
            for (const conditional of conditionals) {
                if (conditional[SETTING_KEYS.sar].length !== 0) {
                    conditional[SETTING_KEYS.sar].forEach((s: any) => {
                        if (s.value === '') {
                            errorFlag = true;
                            if (!errorConStr.includes('SAR')) {
                                errorConStr = errorConStr + ' SAR';
                            }
                        }
                    });
                }

                if (
                    conditional[SETTING_KEYS.spatialGradient].gradients
                        .length !== 0
                ) {
                    conditional[SETTING_KEYS.spatialGradient].gradients.forEach(
                        (sp: any) => {
                            if (
                                sp.value === undefined ||
                                sp.value === null ||
                                sp.value === 0
                            ) {
                                errorFlag = true;
                                if (!errorConStr.includes('SpatialGradient')) {
                                    errorConStr =
                                        errorConStr + ' SpatialGradient';
                                }
                            }
                        }
                    );
                }

                if (conditional[SETTING_KEYS.temp].length !== 0) {
                    conditional[SETTING_KEYS.temp].forEach((t: any) => {
                        if (t.maxRise === '') {
                            errorFlag = true;
                            if (!errorConStr.includes('Temperature')) {
                                errorConStr = errorConStr + ' Temperature';
                            }
                        }
                    });
                }

                if (conditional[SETTING_KEYS.artefacts].length !== 0) {
                    conditional[SETTING_KEYS.artefacts].forEach((art: any) => {
                        if (art.distance === '') {
                            errorFlag = true;
                            if (!errorConStr.includes('Artefacts-Distance')) {
                                errorConStr =
                                    errorConStr + ' Artefacts-Distance';
                            }
                        }
                    });
                }
            }
        }

        if (errorFlag && onValidationError) {
            onValidationError(errorConStr);
        }

        return errorConStr;
    };

    return (
        <>
            {conditionals.length > 0 && (
                <div className="flex flex-col gap-4">
                    <div>
                        <h3 style={{ margin: 0 }}>
                            MRI Conditional Settings ({conditionals.length}{' '}
                            section
                            {conditionals.length !== 1 ? 's' : ''})
                        </h3>
                        <Button
                            type={ButtonType.primary}
                            onClick={addConditional}
                        >
                            Add Conditional Section
                        </Button>
                    </div>
                    {conditionals.map((conditional: any, index: number) => {
                        const conditionalId =
                            conditional._id || conditional.id || index;
                        return (
                            <div key={conditionalId}>
                                <Conditional
                                    index={index}
                                    settings={settings}
                                    conditionals={conditional}
                                    fieldStrengths={fieldStrengths}
                                    autofilledFields={autofilledFields}
                                    showWarningIcon={showWarningIcon}
                                    itemsRef={itemsRef}
                                    onSetShowWarningIcon={onSetShowWarningIcon}
                                    onFieldStrengthChange={(value, checked) =>
                                        handleFieldStrengthChange(
                                            conditionalId,
                                            value,
                                            checked
                                        )
                                    }
                                    onUpdateConditionalField={(
                                        fieldName,
                                        value
                                    ) =>
                                        updateConditionalField(
                                            conditionalId,
                                            fieldName,
                                            value
                                        )
                                    }
                                    onResetHighlights={() => {}}
                                    hasDeleteButton={conditionals.length > 1}
                                />
                            </div>
                        );
                    })}
                </div>
            )}
        </>
    );
};

export default Conditionals;
